/**
 * The re-index confirmation — cost before commitment.
 *
 * Re-indexing re-runs extraction, chunking and EMBEDDING, which is paid for in
 * provider calls and worker time, so no button in either surface (the designer
 * Operations tab, the shared repository header) fires one directly any more.
 * This dialog shows what a run would actually change — how many documents are
 * out of date and how many bytes they weigh, from `client.reindexPending()` —
 * and offers the cheap choice first:
 *
 *  * **Re-index pending only** (default) — just the documents whose index is
 *    missing or stale: never indexed, a new version arrived, the embedding
 *    profile changed, or vectors are missing.
 *  * **Full re-index** — everything, behind a SECOND explicit confirmation,
 *    because it re-embeds the whole corpus including documents that are
 *    already correct.
 *
 * When the host cannot report pending counts (an older server, or a client
 * without `reindexPending`), the dialog says so and still requires the full-run
 * confirmation — the cost warning must not disappear with the estimate.
 */

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { formatBytes, type DmsReindexPending } from './contracts'
import { TYPE } from './styles'

export function ReindexConfirm({ loadPending, onRun, onClose }: {
  /** Absent when the host cannot estimate — the dialog degrades to full-only. */
  loadPending?: () => Promise<DmsReindexPending>
  onRun: (mode: 'pending' | 'full') => void
  onClose: () => void
}) {
  const [pending, setPending] = useState<DmsReindexPending | null>(null)
  const [failed, setFailed] = useState(!loadPending)
  const [confirmFull, setConfirmFull] = useState(false)

  useEffect(() => {
    if (!loadPending) return
    let live = true
    loadPending()
      .then((report) => { if (live) setPending(report) })
      .catch(() => { if (live) setFailed(true) })
    return () => { live = false }
  }, [loadPending])

  const reasonsLine = (report: DmsReindexPending) => {
    const parts: string[] = []
    if (report.reasons.not_indexed) parts.push(`${report.reasons.not_indexed} have never been read`)
    if (report.reasons.profile_changed) parts.push(`${report.reasons.profile_changed} were read using different AI settings`)
    if (report.reasons.missing_embeddings) parts.push(`${report.reasons.missing_embeddings} were read but not finished`)
    return parts.join(', ')
  }

  return createPortal(
    <div style={overlayStyle} onClick={onClose}>
      <div style={panelStyle} role="dialog" aria-modal="true"
           onClick={(event) => event.stopPropagation()}>
        <strong style={{ fontSize: TYPE.heading }}>Rebuild the search</strong>

        {failed ? (
          <p style={textStyle}>
            We cannot tell you how much needs doing here. Rebuilding reads
            <strong> every document</strong> again from scratch, which takes
            time and costs money with your AI provider.
          </p>
        ) : !pending ? (
          <p style={textStyle}>Working out how much needs doing…</p>
        ) : (
          <>
            <p style={textStyle}>
              <strong>{pending.pending_count}</strong> of{' '}
              {pending.documents} document{pending.documents === 1 ? '' : 's'}{' '}
              need{pending.pending_count === 1 ? 's' : ''} to be read again
              {pending.pending_bytes > 0 ? ` (${formatBytes(pending.pending_bytes)})` : ''}.
              {pending.pending_count > 0 && reasonsLine(pending)
                ? <><br /><span style={mutedStyle}>{reasonsLine(pending)}.</span></>
                : null}
              {pending.excluded > 0 ? (
                <><br /><span style={mutedStyle}>
                  {pending.excluded} are left out, because their kind of document
                  is not searchable or is kept away from the AI.
                </span></>
              ) : null}
            </p>
            {pending.pending_count === 0 ? (
              <p style={textStyle}>
                Everything is up to date. There is nothing to do unless you
                want to start again from scratch.
              </p>
            ) : null}
          </>
        )}

        {confirmFull ? (
          <div style={warnBoxStyle}>
            <p style={{ ...textStyle, margin: 0 }}>
              Starting again reads{' '}
              <strong>
                {pending ? `all ${pending.documents} documents` : 'every document'}
                {pending && pending.total_bytes > 0
                  ? ` (${formatBytes(pending.total_bytes)})` : ''}
              </strong>{' '}
              from the beginning — including the ones that are already
              correct. It costs money with your AI provider and can take a
              while. Are you sure?
            </p>
            <div style={rowStyle}>
              <button type="button" style={dangerBtnStyle}
                      onClick={() => onRun('full')}>
                Yes, start again from scratch
              </button>
              <button type="button" style={btnStyle}
                      onClick={() => setConfirmFull(false)}>
                Back
              </button>
            </div>
          </div>
        ) : (
          <div style={rowStyle}>
            {!failed ? (
              <button type="button" style={primaryBtnStyle}
                      disabled={!pending || pending.pending_count === 0}
                      onClick={() => onRun('pending')}>
                Rebuild what needs it
                {pending && pending.pending_count > 0 ? ` (${pending.pending_count})` : ''}
              </button>
            ) : null}
            <button type="button" style={btnStyle}
                    onClick={() => setConfirmFull(true)}>
              Start again from scratch…
            </button>
            <span style={{ flex: 1 }} />
            <button type="button" style={btnStyle} onClick={onClose}>Cancel</button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
}

const panelStyle: React.CSSProperties = {
  background: 'var(--color-bg-secondary)', color: 'var(--color-text)',
  border: '1px solid var(--color-border)', borderRadius: 8,
  padding: 16, width: 'min(440px, 92vw)', display: 'grid', gap: 10,
  boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
}

const textStyle: React.CSSProperties = { fontSize: 14, margin: 0, lineHeight: 1.5 }

const mutedStyle: React.CSSProperties = {
  color: 'var(--color-text-secondary)', fontSize: 13,
}

const rowStyle: React.CSSProperties = {
  display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4,
}

const btnStyle: React.CSSProperties = {
  padding: '5px 10px', fontSize: 13, border: '1px solid var(--color-border)',
  borderRadius: 4, background: 'var(--color-bg-secondary)', color: 'var(--color-text)',
  cursor: 'pointer',
}

const primaryBtnStyle: React.CSSProperties = {
  ...btnStyle, background: 'var(--color-accent)', color: '#fff',
  borderColor: 'var(--color-accent)',
}

const dangerBtnStyle: React.CSSProperties = {
  ...btnStyle, background: 'var(--color-error)', color: '#fff',
  borderColor: 'var(--color-error)',
}

const warnBoxStyle: React.CSSProperties = {
  border: '1px solid var(--color-warning, #d97706)', borderRadius: 6,
  padding: 10, display: 'grid', gap: 8,
  background: 'var(--color-warning-bg, rgba(217, 119, 6, 0.08))',
}
